import { useEffect, useMemo, useRef, useState } from 'react';
import type { PublicEvent } from './discoveryApi';

const configuredEventAssetOrigins: unknown = import.meta.env.VITE_PUBLIC_EVENT_ASSET_ORIGINS;
const configuredMediaAssetOrigins: unknown = import.meta.env.VITE_PUBLIC_MEDIA_ASSET_ORIGINS;
const eventAssetOrigins = [
  typeof configuredEventAssetOrigins === 'string' ? configuredEventAssetOrigins : '',
  typeof configuredMediaAssetOrigins === 'string' ? configuredMediaAssetOrigins : '',
]
  .filter(Boolean)
  .join(',');

const allowedOrigins = new Set(
  eventAssetOrigins
    .split(',')
    .map((value: string) => value.trim())
    .filter(Boolean)
);

function safeUrl(value: string | undefined, kind: 'image' | 'link'): string | undefined {
  if (!value || typeof window === 'undefined') return undefined;
  try {
    const url = new URL(value, window.location.origin);
    if (url.username || url.password) return undefined;
    if (url.origin === window.location.origin && (kind === 'image' || url.pathname.startsWith('/')))
      return url.href;
    return url.protocol === 'https:' && allowedOrigins.has(url.origin) ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function textFromSafeHtml(value?: string) {
  if (!value) return undefined;
  const document = new DOMParser().parseFromString(value, 'text/html');
  document
    .querySelectorAll('script, style, iframe, form, object, embed, svg')
    .forEach((element) => element.remove());
  return document.body.textContent?.trim().slice(0, 500) || undefined;
}

export function EventCarousel({ events }: { events: PublicEvent[] }) {
  const [index, setIndex] = useState(0);
  const [interactionPaused, setInteractionPaused] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef<number | null>(null);
  const dragOffsetX = useRef(0);
  const dragged = useRef(false);
  const dragPointerType = useRef<string | null>(null);
  const [pageVisible, setPageVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden'
  );
  const reducedMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false),
    []
  );
  const count = events.length;

  useEffect(() => {
    if (index >= count) setIndex(0);
  }, [count, index]);

  useEffect(() => {
    const onVisibilityChange = () => setPageVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    if (count < 2 || interactionPaused || !pageVisible || reducedMotion) return undefined;
    const timer = window.setTimeout(() => {
      setIndex((current) => (current + 1) % count);
    }, 6000);
    return () => window.clearTimeout(timer);
  }, [count, index, interactionPaused, pageVisible, reducedMotion]);

  if (!count) return null;

  const move = (delta: number) => {
    setIndex((current) => (current + delta + count) % count);
  };
  const moveTo = (eventIndex: number) => {
    if (eventIndex !== index) setIndex(eventIndex);
  };

  const finishDrag = (clientX: number, carouselWidth: number, cancelled = false) => {
    if (dragStartX.current === null) return;

    const offset = clientX - dragStartX.current;
    const threshold = Math.max(48, carouselWidth * 0.1);
    dragged.current = Math.abs(offset) > 6;
    dragStartX.current = null;
    dragOffsetX.current = 0;
    setDragOffset(0);
    setIsDragging(false);

    if (dragPointerType.current !== 'mouse') setInteractionPaused(false);
    dragPointerType.current = null;

    if (cancelled || Math.abs(offset) < threshold) return;
    move(offset < 0 ? 1 : -1);
  };

  return (
    <section
      className={`event-carousel${isDragging ? ' is-dragging' : ''}`}
      aria-roledescription="carousel"
      aria-label="Sự kiện cửa hàng"
      onMouseEnter={() => setInteractionPaused(true)}
      onMouseLeave={() => {
        if (!isDragging) setInteractionPaused(false);
      }}
      onFocusCapture={() => setInteractionPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setInteractionPaused(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') move(-1);
        if (event.key === 'ArrowRight') move(1);
      }}
      onPointerDown={(event) => {
        if (
          count < 2 ||
          event.button !== 0 ||
          (event.target as Element).closest('a, button')
        ) {
          return;
        }

        dragStartX.current = event.clientX;
        dragOffsetX.current = 0;
        dragged.current = false;
        dragPointerType.current = event.pointerType;
        setIsDragging(true);
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        if (dragStartX.current === null) return;

        const offset = event.clientX - dragStartX.current;
        dragOffsetX.current = offset;
        if (Math.abs(offset) > 6) dragged.current = true;
        setDragOffset(offset);
      }}
      onPointerUp={(event) => {
        finishDrag(event.clientX, event.currentTarget.clientWidth);
      }}
      onPointerCancel={(event) => {
        finishDrag(event.clientX, event.currentTarget.clientWidth, true);
      }}
      onClickCapture={(event) => {
        if (!dragged.current) return;
        event.preventDefault();
        event.stopPropagation();
        dragged.current = false;
      }}
      tabIndex={0}
    >
      <div
        className={`event-track${isDragging ? ' is-dragging' : ''}`}
        style={{ transform: `translate3d(calc(-${index * 100}% + ${dragOffset}px), 0, 0)` }}
      >
        {events.map((item, eventIndex) => {
          const image = safeUrl(item.backgroundImageUrl, 'image');
          const href = safeUrl(item.referenceUrl, 'link');
          const external = href ? new URL(href).origin !== window.location.origin : false;
          const richText = textFromSafeHtml(item.customHtml);
          const active = eventIndex === index;

          return (
            <article
              key={item.id ?? eventIndex}
              className={`event-slide${active ? ' is-active' : ''}`}
              aria-roledescription="slide"
              aria-label={`${eventIndex + 1} / ${count}: ${item.title}`}
              aria-hidden={!active}
              style={
                image
                  ? {
                      backgroundImage: `linear-gradient(90deg, rgb(15 61 46 / .92), rgb(15 61 46 / .36)), url("${image}")`,
                    }
                  : undefined
              }
            >
              <div className="event-slide-content">
                <p className="storefront-section-kicker">
                  Sự kiện nổi bật
                  {count > 1 && (
                    <span className="event-slide-position">
                      {String(eventIndex + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
                    </span>
                  )}
                </p>
                <h2>{item.title}</h2>
                {item.description && <p>{item.description}</p>}
                {richText && <p className="event-rich-copy">{richText}</p>}
                {href ? (
                  <a
                    href={href}
                    target={external ? '_blank' : undefined}
                    rel={external ? 'noopener noreferrer' : undefined}
                    tabIndex={active ? undefined : -1}
                  >
                    Xem chi tiết
                  </a>
                ) : item.id ? (
                  <a href={`/events/${encodeURIComponent(item.id)}`} tabIndex={active ? undefined : -1}>
                    Xem chi tiết
                  </a>
                ) : (
                  <span className="event-unavailable-link">Thông tin đang được cập nhật</span>
                )}
              </div>
              {image ? (
                <span className="visually-hidden">Ảnh nền sự kiện</span>
              ) : (
                <span className="event-art-fallback" aria-hidden="true">
                  ✦
                </span>
              )}
            </article>
          );
        })}
      </div>

      {count > 1 && (
        <div className="event-dots" role="group" aria-label="Chọn sự kiện">
          {events.map((event, eventIndex) => (
            <button
              key={event.id ?? eventIndex}
              type="button"
              className={eventIndex === index ? 'is-active' : undefined}
              aria-current={eventIndex === index ? 'true' : undefined}
              aria-label={`Chuyển đến sự kiện ${eventIndex + 1}: ${event.title}`}
              onClick={() => moveTo(eventIndex)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
